import { useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { TREATMENTS } from "../lib/format";
import { UploadCloud, Loader2, Trash2, Paperclip } from "lucide-react";

export default function BulkScanDialog({ open, onOpenChange, onDone }) {
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef();

  const pick = () => inputRef.current?.click();

  const scan = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 12);
    if (!files.length) return;
    setScanning(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/receipts/scan-bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = data.results.filter((r) => r.ok).map((r) => ({
        include: true, amount: r.amount || "", date: r.date || new Date().toISOString().slice(0, 10),
        description: r.description || r.filename, treatment: r.treatment || "standard",
        attachment_path: r.attachment_path || "", attachment_name: r.attachment_name || "",
      }));
      const failed = data.results.filter((r) => !r.ok).length;
      setRows(parsed);
      toast[failed ? "warning" : "success"](`Scanned ${parsed.length} receipt(s)${failed ? `, ${failed} failed` : ""}`);
    } catch {
      toast.error("Bulk scan failed");
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const createAll = async () => {
    const chosen = rows.filter((r) => r.include && parseFloat(r.amount) > 0 && r.description);
    if (!chosen.length) { toast.error("Nothing to create"); return; }
    setCreating(true);
    try {
      for (const r of chosen) {
        await api.post("/transactions", {
          type: "expense", date: r.date, description: r.description, party: "",
          category: "", treatment: r.treatment, amount: parseFloat(r.amount),
          is_capital: false, has_tax_invoice: true, is_wages: false, payg_withheld: 0,
          super_amount: 0, attachment_path: r.attachment_path, attachment_name: r.attachment_name,
        });
      }
      toast.success(`Created ${chosen.length} expense(s)`);
      setRows([]);
      onOpenChange(false);
      onDone?.();
    } catch {
      toast.error("Could not create some expenses");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setRows([]); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk receipt scan</DialogTitle>
          <DialogDescription>Drop several receipt photos — we read each and turn them into expenses.</DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple onChange={scan} className="hidden" data-testid="bulk-scan-input" />

        {rows.length === 0 ? (
          <button type="button" onClick={pick} disabled={scanning} data-testid="bulk-scan-pick"
            className="w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-12 text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
            {scanning ? <Loader2 className="w-8 h-8 animate-spin" /> : <UploadCloud className="w-8 h-8" />}
            <span className="text-sm font-medium">{scanning ? "Reading receipts…" : "Choose receipt photos (up to 12)"}</span>
            <span className="text-xs">JPG, PNG or WEBP</span>
          </button>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
            {rows.map((r, i) => (
              <div key={i} className="rounded-lg border border-border p-3 grid grid-cols-12 gap-2 items-center" data-testid={`bulk-row-${i}`}>
                <input type="checkbox" checked={r.include} onChange={(e) => setRow(i, "include", e.target.checked)}
                  className="col-span-1 w-4 h-4 accent-[hsl(var(--primary))]" data-testid={`bulk-include-${i}`} />
                <Input value={r.description} onChange={(e) => setRow(i, "description", e.target.value)}
                  className="col-span-4 h-9" placeholder="Description" data-testid={`bulk-desc-${i}`} />
                <Input type="date" value={r.date} onChange={(e) => setRow(i, "date", e.target.value)} className="col-span-3 h-9" />
                <Input type="number" step="0.01" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)}
                  className="col-span-2 h-9" placeholder="0.00" data-testid={`bulk-amount-${i}`} />
                <Select value={r.treatment} onValueChange={(v) => setRow(i, "treatment", v)}>
                  <SelectTrigger className="col-span-1 h-9 px-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TREATMENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.short}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => removeRow(i)} className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center">
                  <Trash2 className="w-4 h-4" />
                </button>
                {r.attachment_path && (
                  <a href={fileUrl(r.attachment_path)} target="_blank" rel="noreferrer" className="col-span-12 text-xs text-primary inline-flex items-center gap-1 hover:underline">
                    <Paperclip className="w-3 h-3" /> {r.attachment_name}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {rows.length > 0 && <Button variant="outline" onClick={pick} disabled={scanning}>Add more</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {rows.length > 0 && (
            <Button onClick={createAll} disabled={creating} data-testid="bulk-create-btn">
              {creating ? "Creating…" : `Create ${rows.filter((r) => r.include).length} expense(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
